import { useCallback, useEffect, useState } from 'react';
import { playSound, type SoundName } from './sound';

/**
 * Which sound each alert makes, and how loud.
 *
 * Kept in localStorage rather than the database because it is a property of the
 * DEVICE, not of the account: the same person wants this on at their desk and
 * silent on a phone in a meeting, and a server-side setting would follow them
 * between the two.
 *
 * Keyed by alert so each can be set — or silenced — on its own. Only the knot
 * alert is wired up so far; the others read their default until they are.
 */
export type AlertKind = 'knot' | 'knotPending' | 'point' | 'price';

export interface SoundPrefs {
  sounds: Record<AlertKind, SoundName>;
  /** 0..1, shared by every alert. */
  volume: number;
  /**
   * How many times a price alert sounds. 0 = keep sounding until the card is
   * dismissed, like a terminal alarm.
   */
  priceRepeat: number;
  /** Which set of defaults this was written against. See `read`. */
  version?: number;
}

const STORAGE_KEY = 'pipyar-alert-sounds';

/**
 * Bumped when a NEW alert is given a sound it did not previously have.
 *
 * A stored preference wins over the defaults, which is right — except on the
 * turn an alert goes from having no sound at all to having one. Anyone who had
 * opened the sound settings before that would have `none` saved for it, not as
 * a decision but as the absence of one, and would see the feature do nothing.
 * The upgrade below fills those in once; after that the stored choice, silence
 * included, is respected for good.
 */
const DEFAULTS_VERSION = 4;

/*
 * Each alert gets a distinguishable sound by default.
 *
 * Chime and Rise are chosen to be told apart without looking: Rise climbs,
 * which suits something approaching, while the knot's Chime is a flat interval
 * for something that has already happened. Price alerts get Bell: a line you placed
 * yourself is the one alert you have explicitly asked to be interrupted by.
 */
const DEFAULTS: SoundPrefs = {
  sounds: { knot: 'chime', knotPending: 'blip', point: 'rise', price: 'bell' },
  volume: 0.6,
  priceRepeat: 0,
};

/** Choices offered in settings; 0 means until dismissed. */
export const PRICE_REPEAT_OPTIONS = [1, 3, 5, 10, 0] as const;

function read(): SoundPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<SoundPrefs>;
    // Merged over the defaults, then trimmed to the kinds that still exist: a
    // preference saved for a removed alert (the old Fibonacci one) is dropped.
    const merged: Record<string, SoundName> = { ...DEFAULTS.sounds, ...(parsed.sounds ?? {}) };
    const stored = Object.fromEntries(
      Object.keys(DEFAULTS.sounds).map((k) => [k, merged[k]!]),
    ) as Record<AlertKind, SoundName>;

    // Only ever turns silence into the new default, and only for a preference
    // written before that default existed. A sound already chosen is untouched.
    if ((parsed.version ?? 1) < 2) {
      if (stored.point === 'none') stored.point = DEFAULTS.sounds.point;
    }
    if ((parsed.version ?? 1) < 3) {
      if (stored.price === 'none') stored.price = DEFAULTS.sounds.price;
    }
    if ((parsed.version ?? 1) < 4) {
      if (stored.knotPending === 'none') stored.knotPending = DEFAULTS.sounds.knotPending;
    }

    return {
      // Merged rather than replaced, so a preference saved before a new alert
      // kind existed does not leave that kind undefined.
      sounds: stored,
      volume:
        typeof parsed.volume === 'number' && parsed.volume >= 0 && parsed.volume <= 1
          ? parsed.volume
          : DEFAULTS.volume,
      priceRepeat:
        typeof parsed.priceRepeat === 'number' && Number.isInteger(parsed.priceRepeat) && parsed.priceRepeat >= 0
          ? parsed.priceRepeat
          : DEFAULTS.priceRepeat,
    };
  } catch {
    return DEFAULTS;
  }
}

/** Broadcasts a change to every mounted hook, including in this same tab. */
const CHANGED = 'pipyar-alert-sounds-changed';

export function useAlertSound(kind: AlertKind) {
  const [prefs, setPrefs] = useState<SoundPrefs>(read);

  useEffect(() => {
    const sync = () => setPrefs(read());
    window.addEventListener(CHANGED, sync);
    // `storage` only fires in OTHER tabs, which is exactly what it is for here.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const play = useCallback(() => {
    playSound(prefs.sounds[kind], prefs.volume);
  }, [prefs, kind]);

  return { prefs, play };
}

/** Read the current preferences without subscribing. */
export function readSoundPrefs(): SoundPrefs {
  return read();
}

export function writeSoundPrefs(next: SoundPrefs): void {
  try {
    // Stamped on save, so a deliberate choice of silence is never re-upgraded.
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...next, version: DEFAULTS_VERSION }));
  } catch {
    /* private mode — the choice just will not persist. */
  }
  window.dispatchEvent(new Event(CHANGED));
}
